import { FunctionComponent, JSX, useState } from 'react'
import { Add, Remove } from '@mui/icons-material'
import styles from './mealHandler.module.scss'
import { useTranslation } from 'react-i18next'
import { MealDetail, MealHabit, MealMenu, MealQuantityChild, MealTexture } from '@/types/Meal'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import ShowMealChildrenDialog from '@/container/meal/mealHandler/Dialog/ShowMealChildrenDialog.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface MealDetailRowProps {
    detail: MealDetail
    canUpdate: boolean
    disabled?: boolean
    mealFrozen: boolean
    orderAdjustment: number
    onAdjustOrder: (delta: number) => void
}

const MealDetailRow: FunctionComponent<MealDetailRowProps> = ({
    detail,
    canUpdate,
    disabled = false,
    mealFrozen,
    orderAdjustment,
    onAdjustOrder
}: MealDetailRowProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess } = useNotification()

    const [isShowChildsDialogOpen, setShowChildsDialogOpen] = useState(false)
    const [mealQuantityChilds, setMealQuantityChilds] = useState<MealQuantityChild[]>([])
    const [dialogTitle, setDialogTitle] = useState('')

    const hasNoSpecificity: boolean =
        detail.combination.menus.length === 0 &&
        detail.combination.textures.length === 0 &&
        detail.combination.habits.length === 0

    function openChildsDialog(quantity: MealQuantityChild[], title: string): void {
        if (quantity.length === 0) {
            showNotificationSuccess(t('noChilds'))
        } else {
            setDialogTitle(`${quantity.length} ${title}`)
            setShowChildsDialogOpen(true)
            setMealQuantityChilds(quantity)
        }
    }

    function closeChildsDialog(): void {
        setShowChildsDialogOpen(false)
        setMealQuantityChilds([])
    }

    const reserved: number = detail.quantityReserved.length
    const toOrder: number = reserved + orderAdjustment
    const frozenTotal: number = detail.quantityReserved.length + detail.quantityAdded
    const realTotal: number = detail.quantityReal.length
    const difference: number = frozenTotal - realTotal

    const orderColorClass: string =
        toOrder > reserved ? styles.QuantityOrderMore : toOrder < reserved ? styles.QuantityOrderLess : ''

    return (
        <div key={getMealDetailKey(detail)} className={styles.MealRow}>
            {mealFrozen ? (
                <div className={styles.FrozenBadges}>
                    <div className={styles.FrozenBadgesRow}>
                        <div
                            className={`${styles.FrozenBadge} ${styles.FrozenBadgeFrozen}`}
                            onClick={() =>
                                openChildsDialog(
                                    detail.quantityReserved,
                                    detail.quantityReserved.length > 1 ? t('reserved') : t('oneReserved')
                                )
                            }
                        >
                            <span className={styles.QuantityNumber}>{frozenTotal}</span>
                            <span className={styles.QuantityLabel}>{t('ordered')}</span>
                        </div>
                        <div
                            className={`${styles.FrozenBadge} ${styles.FrozenBadgeReal}`}
                            onClick={() => openChildsDialog(detail.quantityReal, t('real'))}
                        >
                            <span className={styles.QuantityNumber}>{realTotal}</span>
                            <span className={styles.QuantityLabel}>{t('real')}</span>
                        </div>
                    </div>
                    {difference !== 0 && (
                        <div
                            className={`${styles.DifferenceRow} ${difference > 0 ? styles.DifferenceRowSufficient : styles.DifferenceRowInsufficient}`}
                        >
                            <span
                                className={`${styles.DifferenceValue} ${difference > 0 ? styles.DifferenceSufficient : styles.DifferenceInsufficient}`}
                            >
                                {difference > 0 ? `+${difference}` : difference}
                            </span>
                            <span className={styles.DifferenceLabel}>{t('difference')}</span>
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.FrozenBadges}>
                    <div className={styles.FrozenBadgesRow}>
                        <div
                            className={`${styles.FrozenBadge} ${styles.FrozenBadgeFrozen} ${styles.FrozenBadgeNoPointer} ${toOrder > reserved ? styles.QuantityBadgeMore : toOrder < reserved ? styles.QuantityBadgeLess : ''}`}
                        >
                            <div className={styles.AddRemoveControls}>
                                {canUpdate && (
                                    <ButtonIcon
                                        icon={<Remove />}
                                        onClick={() => onAdjustOrder(-1)}
                                        disabled={disabled || toOrder <= 0}
                                        className={styles.ButtonSmall}
                                    />
                                )}
                                <span className={`${styles.QuantityNumber} ${orderColorClass}`}>{toOrder}</span>
                                {canUpdate && (
                                    <ButtonIcon
                                        icon={<Add />}
                                        onClick={() => onAdjustOrder(1)}
                                        disabled={disabled}
                                        className={styles.ButtonSmall}
                                    />
                                )}
                            </div>
                            <span className={styles.QuantityLabel}>{t('toOrder')}</span>
                        </div>
                        <div
                            className={`${styles.FrozenBadge} ${styles.FrozenBadgeReal}`}
                            onClick={() =>
                                openChildsDialog(
                                    detail.quantityReserved,
                                    reserved > 1 ? t('reserved') : t('oneReserved')
                                )
                            }
                        >
                            <span className={styles.QuantityNumber}>{reserved}</span>
                            <span className={styles.QuantityLabel}>
                                {reserved > 1 ? t('reserved') : t('oneReserved')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.MealDetails}>
                {hasNoSpecificity && (
                    <div className={styles.MealCategory}>
                        <div className={styles.CategoryTags}>
                            <span className={styles.TagStandard}>{t('mealWithoutSpecificity')}</span>
                        </div>
                    </div>
                )}
                {detail.combination.menus.length > 0 && (
                    <div className={styles.MealCategory}>
                        <span className={styles.CategoryLabel}>{t('menu')}</span>
                        <div className={styles.CategoryTags}>
                            {detail.combination.menus.map(
                                (m: MealMenu): JSX.Element => (
                                    <span key={m.id} className={styles.TagMenu}>
                                        {m.menu}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}
                {detail.combination.textures.length > 0 && (
                    <div className={styles.MealCategory}>
                        <span className={styles.CategoryLabel}>{t('textures')}</span>
                        <div className={styles.CategoryTags}>
                            {detail.combination.textures.map(
                                (tex: MealTexture): JSX.Element => (
                                    <span key={tex.id} className={styles.TagTextureHabit}>
                                        {tex.texture}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}
                {detail.combination.habits.length > 0 && (
                    <div className={styles.MealCategory}>
                        <span className={styles.CategoryLabel}>{t('habits')}</span>
                        <div className={styles.CategoryTags}>
                            {detail.combination.habits.map(
                                (e: MealHabit): JSX.Element => (
                                    <span key={e.id} className={styles.TagTextureHabit}>
                                        {e.habit}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isShowChildsDialogOpen && mealQuantityChilds && (
                <ShowMealChildrenDialog
                    open={isShowChildsDialogOpen}
                    handleClose={closeChildsDialog}
                    childs={mealQuantityChilds}
                    title={dialogTitle}
                />
            )}
        </div>
    )
}

export default MealDetailRow

export function getMealDetailKey(detail: MealDetail): string {
    const menuIds: string = detail.combination.menus
        .map((m: MealMenu): number => m.id)
        .sort(sortIds)
        .join('-')
    const textureIds: string = detail.combination.textures
        .map((t: MealTexture): number => t.id)
        .sort(sortIds)
        .join('-')
    const habitIds: string = detail.combination.habits
        .map((e: MealHabit): number => e.id)
        .sort(sortIds)
        .join('-')
    return `m${menuIds}_t${textureIds}_h${habitIds}`
}

function sortIds(a: number, b: number): number {
    return a - b
}
